import React from 'react'
import {
  AttributeFilterValues,
  FilterDefinition,
  FilterState,
  FiltersValues,
  KeyValuesFilterValue
} from '../../../../types'
import { useSearchParams } from 'react-router-dom'
import { isFilterSet, updateFilterState, readUrlParams, updateUrlParams } from './utils'
import { FiltersProps } from '../filters'
import { FILTER_TYPES } from '../constants'
import { DateRangeSelection } from '@contrast-security-inc/design-system-inputs'

const getEmptyFilterValue = (filterType: string) => {
  if (filterType === FILTER_TYPES.SINGLE) {
    return ''
  } else if (filterType === FILTER_TYPES.DATE) {
    return undefined
  } else {
    return []
  }
}

export const createDefaultFilterState = (
  filterDefs: FilterDefinition[],
  urlParams: URLSearchParams | null = null,
  prevFilters?: FiltersValues
) =>
  filterDefs.reduce((acc, filterDef) => {
    const { defaultValue, filterKey, filterType } = filterDef

    const paramValue = urlParams ? readUrlParams(filterDef, urlParams, prevFilters) : undefined

    let filterValue:
      | string
      | string[]
      | DateRangeSelection
      | undefined
      | AttributeFilterValues
      | KeyValuesFilterValue[] = []

    if (paramValue) {
      filterValue = paramValue
    } else if (defaultValue) {
      filterValue = defaultValue
    } else {
      filterValue = getEmptyFilterValue(filterType)
    }

    return {
      ...acc,
      [filterKey]: filterValue
    }
  }, {})

export const useFilters = ({ filterDefs }: { filterDefs: FilterDefinition[] }): FiltersProps => {
  const [searchParams, setSearchParams] = useSearchParams()
  // keeps the url clean by only updating once the filters have changed
  const [urlUpdateState, setUrlUpdateState] = React.useState<{
    doUpdate?: boolean
    replaceUrlHistory?: boolean
  }>({
    doUpdate: false,
    replaceUrlHistory: false
  })
  const [filters, setFilters] = React.useState<FiltersValues>(
    /**
      The initial state of the filters can be loaded from URL search params to support direct links that are filter aware.
      These links are used from the dashboard to go directly to more detailed views (based on your dashboard filters).

      After this initial load there is one source of truth, the filters state.
      The filter state stores additional information that the URL searchParams cannot support (Could you add detail here?).
      We do keep the URL searchParams in sync though so that users can bookmark the URL and return to the view.
    */
    React.useMemo(
      () => createDefaultFilterState(filterDefs, searchParams),
      // only set the default onMount
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filterDefs]
    )
  )

  // handles non-static filter defs loading from backend
  // handles browser back/forward navigation
  React.useEffect(() => {
    if (filterDefs.length !== 0) {
      const newFilters = createDefaultFilterState(filterDefs, searchParams, filters)

      // stringify as a workaround for deep object comparison
      // (Dan Abramov approved :)) https://github.com/facebook/react/issues/14476#issuecomment-471199055
      if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
        setFilters(newFilters)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDefs, searchParams])

  /**
   * filterKey: the key of the filter to update
   * filterState: the new state of the filter
   * options.clearExistingFilters: if true, existing values will be cleared for this filter prior to setting the new state
   * options.replaceUrlHistory: if true, the current browser history entry will be replaced instead of adding a new one
   */
  const updateFilter: FiltersProps['updateFilter'] = React.useCallback(
    (filterKey, filterState, { clearExistingFilters = false, replaceUrlHistory = false } = {}) => {
      const filterStates = Array.isArray(filterState) ? filterState : [filterState]
      const filterDef = filterDefs.find((it) => it.filterKey === filterKey)

      if (!filterDef) {
        console.error(`Filter key [${filterKey}] not found while updating filter`)
        return
      }

      let filtersToUpdate = filters

      // clear existing values if clearExistingFilters is true
      if (clearExistingFilters) {
        const emptyFilterValue = getEmptyFilterValue(filterDef.filterType)

        filtersToUpdate = {
          ...filtersToUpdate,
          ...({ [filterKey]: emptyFilterValue } as FiltersValues)
        }
      }

      // @ts-ignore
      const updatedFilters = filterStates.reduce<ReturnType<typeof updateFilterState>>(
        // @ts-ignore
        (acc, filterState: FilterState) => {
          return {
            ...acc,
            ...updateFilterState(filterDef, { filters: acc, filterKey, filterState })
          }
        },
        filtersToUpdate
      )
      if (updatedFilters) {
        setFilters(updatedFilters)
        setUrlUpdateState({ doUpdate: true, replaceUrlHistory })
      }
    },
    [filterDefs, filters]
  )

  const hasFilters = React.useCallback(() => {
    return Object.keys(filters)
      .filter((key) => !filterDefs.find((it) => it.filterKey === key)?.alwaysSet)
      .some((key) => isFilterSet(filters[key]))
  }, [filterDefs, filters])

  const resetAll = React.useCallback(() => {
    const updatedFilters = createDefaultFilterState(filterDefs)

    setFilters(updatedFilters)
    setUrlUpdateState({ doUpdate: true })
  }, [filterDefs])

  const resetFilter = React.useCallback(
    (filterKey: string) => {
      const matchingFilter = filterDefs.find((it) => it.filterKey === filterKey)
      const matchingFilterDefault = matchingFilter
        ? createDefaultFilterState([matchingFilter])
        : undefined

      const updatedFilters = {
        ...filters,
        ...(matchingFilterDefault ?? {})
      }

      setFilters(updatedFilters)
      setUrlUpdateState({ doUpdate: true })
    },
    [filterDefs, filters]
  )

  // update inside use effect instead of immediately after filters state update
  // to avoid conflicts with external url updates.
  React.useEffect(() => {
    if (urlUpdateState.doUpdate) {
      const newParams = updateUrlParams(searchParams, filterDefs, filters)
      if (newParams.toString() !== searchParams.toString()) {
        setSearchParams(newParams, {
          replace: urlUpdateState.replaceUrlHistory ?? false
        })
      }
      setUrlUpdateState({ doUpdate: false }) //reset the flag
    }
  }, [urlUpdateState, filterDefs, filters, searchParams, setSearchParams])

  return {
    filterDefs,
    filters,
    updateFilter,
    hasFilters,
    resetAll,
    resetFilter
  }
}
